import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { getArticleById, getArticlesByCategory, articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <p className="text-foreground-500">文章不存在</p>
      </div>
    );
  }

  const relatedArticles = getArticlesByCategory(article.categorySlug)
    .filter(a => a.id !== article.id)
    .slice(0, 3);

  const otherRecent = articles
    .filter(a => a.id !== article.id && a.categorySlug !== article.categorySlug)
    .slice(0, 3);

  return (
    <>
      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.excerpt,
        image: article.coverImage,
        datePublished: article.publishDate,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        publisher: {
          '@type': 'Organization',
          name: siteInfo.name,
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://51chigua.com/article/${article.id}`,
        },
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51chigua.com' },
          { '@type': 'ListItem', position: 2, name: article.category, item: `https://51chigua.com/category/${article.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <article className="px-4 md:px-6 py-8">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]} />

              <header className="mt-6 mb-8">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700 font-medium hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {article.category}
                  </Link>
                  {article.isHot && (
                    <span className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                      热门
                    </span>
                  )}
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2 py-1 rounded-full bg-background-100 text-foreground-500 whitespace-nowrap">
                      #{tag}
                    </span>
                  ))}
                </div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 leading-snug mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-400 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万阅读` : `${article.readCount}阅读`}
                  </span>
                </div>
              </header>

              <div className="mb-10 rounded-xl overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - 51吃瓜官网`}
                  className="w-full h-auto object-cover"
                />
              </div>

              <div
                className="prose prose-base max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-6 border-t border-background-200/70">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-10 flex items-center justify-between bg-background-100 rounded-lg px-5 py-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{article.category}
                </Link>
                <Link
                  to="/"
                  className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-700 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-4-line"></i>
                  返回首页
                </Link>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="px-4 md:px-6 py-10 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <h2 className="font-heading text-xl text-foreground-900 mb-6">
                  相关{article.category}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Other Recent */}
          {otherRecent.length > 0 && (
            <section className="px-4 md:px-6 py-10">
              <div className="max-w-7xl mx-auto">
                <h2 className="font-heading text-xl text-foreground-900 mb-6">更多吃瓜</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {otherRecent.map((a) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}